/**
 * BackgroundDropzone — V14.62 Sprint 20 (Lab).
 *
 * Charge un vrai plan (JPG/PNG) en background. Le fichier est lu localement
 * via FileReader → base64 dataURL → injecté dans le store. Aucun upload
 * réseau : conforme à la promesse « Edge computing / aucune image ne quitte
 * le navigateur ».
 *
 * Modes d'utilisation :
 *   • Clic sur le bouton « Importer un plan »
 *   • Drag-and-drop sur la zone elle-même (overlay highlight)
 *
 * Contraintes :
 *   • Types acceptés : image/jpeg, image/png, image/webp (compatibles Konva)
 *   • Limite indicative 12 Mo pour éviter d'exploser la mémoire navigateur
 *   • Si une image est déjà chargée, propose de la remplacer
 */
import { useRef, useState } from 'react';
import { UploadCloud, Image as ImageIcon, X } from 'lucide-react';
import { toast } from 'sonner';
import { useGeometryStore } from './useGeometryStore';

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 12 * 1024 * 1024;

const BackgroundDropzone = () => {
  const backgroundImage = useGeometryStore((s) => s.backgroundImage);
  const setBackgroundImage = useGeometryStore((s) => s.setBackgroundImage);
  const clearBackgroundImage = useGeometryStore((s) => s.clearBackgroundImage);
  const resetViewport = useGeometryStore((s) => s.resetViewport);
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      toast.error('Format non supporté — JPG, PNG ou WEBP uniquement');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Fichier trop volumineux (max ${Math.round(MAX_BYTES / 1024 / 1024)} Mo)`);
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result;
      if (typeof dataUrl !== 'string') return;
      // Probe natural dimensions before committing to the store so the
      // Konva stage can resize accordingly without an extra round-trip.
      const img = new Image();
      img.onload = () => {
        setBackgroundImage({
          dataUrl,
          width: img.naturalWidth,
          height: img.naturalHeight,
          name: file.name,
        });
        resetViewport();
        toast.success(`Plan importé : ${file.name} (${img.naturalWidth}×${img.naturalHeight})`);
      };
      img.onerror = () => toast.error('Image illisible');
      img.src = dataUrl;
    };
    reader.onerror = () => toast.error('Lecture du fichier impossible');
    reader.readAsDataURL(file);
  };

  const onPick = (e) => handleFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  if (backgroundImage) {
    return (
      <div className="inline-flex items-center gap-2 h-8 px-2.5 rounded text-[11px] border bg-emerald-500/10 border-emerald-400/40 text-emerald-200"
           data-testid="background-image-loaded">
        <ImageIcon className="w-3.5 h-3.5" />
        <span className="font-mono truncate max-w-[180px]" title={backgroundImage.name}>
          {backgroundImage.name}
        </span>
        <span className="text-emerald-300/60 font-mono">
          {backgroundImage.width}×{backgroundImage.height}
        </span>
        <button type="button"
                onClick={() => inputRef.current?.click()}
                className="ml-1 text-[10px] underline hover:text-white"
                data-testid="background-replace-btn">
          Remplacer
        </button>
        <button type="button"
                onClick={clearBackgroundImage}
                className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded hover:bg-white/10"
                title="Retirer le plan de fond"
                data-testid="background-clear-btn">
          <X className="w-3 h-3" />
        </button>
        <input ref={inputRef} type="file" accept={ACCEPTED.join(',')} className="hidden"
               onChange={onPick} data-testid="background-input" />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      data-testid="background-dropzone"
      className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded text-[11px] border transition-colors ${
        dragOver
          ? 'bg-emerald-500/20 border-emerald-400 text-emerald-100'
          : 'bg-amber-500/10 border-amber-400/40 text-amber-200 hover:bg-amber-500/15'
      }`}
      title="Importer un plan JPG/PNG/WEBP — ou glissez-déposez ici"
    >
      <UploadCloud className="w-3.5 h-3.5" />
      <span>Importer un plan</span>
      <input ref={inputRef} type="file" accept={ACCEPTED.join(',')} className="hidden"
             onChange={onPick} data-testid="background-input" />
    </button>
  );
};

export default BackgroundDropzone;
